import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { Eyebrow } from "../components/Reveal";
import { trackOnce } from "../analytics/ga4";

import API from "../api";

export default function PaymentResultPage({ kind }) {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [status, setStatus] = useState(kind === "cancel" ? "cancelled" : "checking");
  const [paidKind, setPaidKind] = useState("gift_voucher");
  const tries = useRef(0);

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    let stop = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setStatus("paid");
          setPaidKind(data.kind || "gift_voucher");
          trackOnce(`pay-${sessionId}`, "purchase", {
            transaction_id: sessionId,
            payment_kind: data.kind || "gift_voucher",
          });
          return;
        }
      } catch {
        /* keep polling */
      }
      tries.current += 1;
      if (tries.current < 12 && !stop) setTimeout(poll, 2500);
      else if (!stop) setStatus("timeout");
    };
    poll();
    return () => {
      stop = true;
    };
  }, [kind, sessionId]);

  return (
    <PageLayout
      seo={{
        title: kind === "cancel" ? "Payment cancelled — Collanmore Island Lodge" : "Thank you — Collanmore Island Lodge",
        description: "Collanmore Island Lodge gift vouchers.",
        path: kind === "cancel" ? "/payment/cancel" : "/payment/success",
        noindex: true,
      }}
    >
      <section className="flex min-h-[70vh] items-center justify-center px-5 pt-28 pb-16">
        <div className="max-w-xl text-center">
          {kind === "cancel" ? (
            <>
              <XCircle className="mx-auto h-12 w-12 text-navy/40" />
              <Eyebrow>Payment cancelled</Eyebrow>
              <h1 className="mt-5 font-display text-3xl font-medium text-navy md:text-4xl">No money has moved</h1>
              <p className="mt-4 text-sm font-light leading-relaxed text-navy/70">
                Your voucher wasn't purchased and nothing was charged. If you'd like to try again, just
                head back to the gift vouchers page.
              </p>
              <a
                href="/gift-vouchers/"
                data-testid="payment-cancel-retry"
                className="mt-8 inline-block bg-gold px-8 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2"
              >
                Back to gift vouchers
              </a>
            </>
          ) : status === "paid" ? (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" data-testid="payment-success-icon" />
              <Eyebrow>{paidKind === "activity_booking" ? "Adventure booked" : "Voucher purchased"}</Eyebrow>
              <h1 className="mt-5 font-display text-3xl font-medium text-navy md:text-4xl">
                {paidKind === "activity_booking" ? "Your adventure is booked in" : "The island is theirs"}
              </h1>
              <p className="mt-4 text-sm font-light leading-relaxed text-navy/70" data-testid="payment-success-message">
                {paidKind === "activity_booking"
                  ? "Your payment went through and a confirmation is on its way by email. We'll be in touch to confirm your exact start time and meeting point."
                  : "Your payment went through and the gift voucher is on its way by email, with a unique code to quote when booking. If it hasn't arrived within a few minutes, check the spam folder — or contact us and we'll resend it straight away."}
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-gold" />
              <h1 className="mt-6 font-display text-3xl font-medium text-navy" data-testid="payment-checking">
                Confirming your payment…
              </h1>
              <p className="mt-3 text-sm font-light text-navy/70">This only takes a moment — please don't close the page.</p>
            </>
          )}
          {status === "timeout" && (
            <p className="mt-5 text-xs text-navy/60">
              Still confirming with the bank — this can take a minute. You'll get a confirmation email
              either way.
            </p>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
